import axios, { AxiosError } from 'axios';
import AttToken from './AttToken';
import { FluxoConfig } from '../types/FluxoConfig';

export type RegraCadastroApi = {
  id?: string;
  idRegraCadastro?: string;
  idModeloCadastro?: string;
  modeloCadastroId?: string;
  nome?: string;
};

export type ModeloAgendamentoApi = {
  regrasCadastro?: RegraCadastroApi[];
};

export type ModeloCadastroApi = {
  campos?: Array<{ nome?: string; id?: string; obrigatorio?: boolean }> | Record<string, unknown>;
};

export class FluxoConfigError extends Error {
  constructor(public status?: number, message = 'Não foi possível concluir a operação.') {
    super(message);
  }
}

function baseUrl() {
  const base = process.env.REACT_APP_EMPRESA_API_BASE_URL;
  if (!base) throw new FluxoConfigError(undefined, 'URL do serviço Empresa não configurada.');
  return base.replace(/\/$/, '');
}

async function headersFor(empresa: string, empresaAlvo = false) {
  const auth = await AttToken();
  if (!auth) throw new FluxoConfigError(401, 'Sessão expirada. Entre novamente.');
  return {
    usertoken: auth.userToken,
    username: auth.username,
    userempresa: auth.userEmpresa,
    'Content-Type': 'application/json',
    ...(empresaAlvo ? { empresaid: empresa } : {}),
  };
}

function asSafeError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const status = axiosError.response?.status;
    const message = axiosError.response?.data?.message || (
      status === 404 ? 'Configuração ativa não encontrada.' :
      status === 409 ? 'Há múltiplas configurações; a consulta por Fluxo ainda não está disponível.' :
      status === 401 ? 'Sessão expirada. Entre novamente.' :
      status === 403 ? 'Você não tem autorização para esta empresa ou serviço.' :
      status ? 'Não foi possível concluir a operação.' : 'Falha de rede. Confira o resultado antes de tentar novamente.'
    );
    throw new FluxoConfigError(status, message);
  }
  throw error;
}

export async function consultarConfigUnica(empresa: string): Promise<FluxoConfig> {
  try {
    const headers = await headersFor(empresa);
    const { data } = await axios.get<{ success: boolean; config: FluxoConfig }>(
      `${baseUrl()}/fluxo-config/${encodeURIComponent(empresa)}`, { headers }
    );
    return data.config;
  } catch (error) { return asSafeError(error); }
}

export async function salvarConfig(empresa: string, config: FluxoConfig): Promise<FluxoConfig> {
  try {
    const headers = await headersFor(empresa);
    const { data } = await axios.put<{ success: boolean; config: FluxoConfig }>(
      `${baseUrl()}/fluxo-config/${encodeURIComponent(empresa)}`, config, { headers }
    );
    return data.config;
  } catch (error) { return asSafeError(error); }
}

export async function consultarModeloAgendamento(
  empresa: string,
  idModelo: string
): Promise<ModeloAgendamentoApi> {
  try {
    const headers = await headersFor(empresa, true);
    const { data } = await axios.get<{ agendamentoModelo: ModeloAgendamentoApi }>(
      `${baseUrl()}/agendamento-modelo/${encodeURIComponent(idModelo)}`, { headers }
    );
    return data.agendamentoModelo;
  } catch (error) { return asSafeError(error); }
}

export async function consultarModeloCadastro(
  empresa: string,
  idModelo: string
): Promise<ModeloCadastroApi> {
  try {
    const headers = await headersFor(empresa, true);
    const { data } = await axios.get<{ cadastroModelo: ModeloCadastroApi }>(
      `${baseUrl()}/cadastro-modelo/${encodeURIComponent(idModelo)}`, { headers }
    );
    return data.cadastroModelo;
  } catch (error) { return asSafeError(error); }
}
